import { useState } from 'react';
import { StickyNote } from 'lucide-react';
import API from '../../lib/api';

export default function NotesList({ notes = [], orderId, steps = [], onRefresh }) {
  const [note, setNote]         = useState('');
  const [stepNum, setStepNum]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const handleAdd = async () => {
    if (!note.trim()) return;
    setError('');
    setSaving(true);
    try {
      await API.post(`/admin/orders/${orderId}/notes`, {
        note: note.trim(),
        step_number: stepNum ? Number(stepNum) : undefined,
      });
      setNote('');
      setStepNum('');
      if (onRefresh) await onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save note');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-5">
      {/* Existing notes */}
      {notes.length === 0 ? (
        <p className="text-sm text-[#9CA3AF] font-body">No notes yet</p>
      ) : (
        <div className="space-y-3">
          {notes.map((n) => (
            <div key={n.id} className="bg-[#FFFBF0] border border-yellow-100 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <StickyNote className="h-3.5 w-3.5 text-yellow-500" />
                  <span className="text-xs font-body font-medium text-[#1A1A2E]">
                    {n.admin_name || 'Admin'}
                  </span>
                  {n.step_number && (
                    <span className="text-[10px] font-body text-[#6C63FF] bg-[#6C63FF]/10 px-1.5 py-0.5 rounded-full">
                      Step {n.step_number}
                    </span>
                  )}
                </div>
                <span className="text-[10px] text-[#9CA3AF] font-body">
                  {new Date(n.created_at).toLocaleString('en-IN')}
                </span>
              </div>
              <p className="text-sm font-body text-[#4A4A60]">{n.note}</p>
            </div>
          ))}
        </div>
      )}

      {/* Add note form */}
      <div className="bg-white border border-[#E8E6F8] rounded-xl p-4 space-y-3">
        <p className="text-sm font-heading font-semibold text-[#1A1A2E]">Add Note</p>

        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Internal note about this order…"
          className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg p-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        />

        <div className="flex items-center gap-3">
          <select
            value={stepNum}
            onChange={(e) => setStepNum(e.target.value)}
            className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
          >
            <option value="">No step (general note)</option>
            {steps.map((s) => (
              <option key={s.step_number} value={s.step_number}>
                Step {s.step_number}: {s.name}
              </option>
            ))}
          </select>

          <button
            onClick={handleAdd}
            disabled={saving || !note.trim()}
            className="px-4 py-2 text-sm font-body font-medium bg-[#6C63FF] hover:bg-[#5B52CC] text-white rounded-lg transition-colors disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Add Note'}
          </button>
        </div>

        {error && <p className="text-xs text-red-500 font-body">{error}</p>}
      </div>
    </div>
  );
}
